import { ref } from 'vue'
import { defineStore } from 'pinia'
import * as Sentry from '@sentry/browser'
import { getAuth, signInWithEmailAndPassword, signInWithCustomToken } from 'firebase/auth'
import { RequestService } from '../services/RequestService'
import { handleErrors } from '../utils/errorHandler'
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  isPlatformAdmin as checkPlatformAdmin
} from '../access/hasPermission'
import { logLoginSuccess, logTokenRefresh, logLoginFailed } from '../services/AuditLogService'

const userProfileApiUrl = () => {
  const base = import.meta.env.VITE_FUNCTIONS_BASE_URL
  if (!base) {
    throw new Error('VITE_FUNCTIONS_BASE_URL is not defined')
  }
  return `${base}/usersGateway/api/v1/users/me`
}

/* Two-factor authentication. */
const mfaApiUrl = (path) => {
  const base = import.meta.env.VITE_FUNCTIONS_BASE_URL
  if (!base) {
    throw new Error('VITE_FUNCTIONS_BASE_URL is not defined')
  }
  return `${base}/mfaGateway/api/v1/mfa/${path}`
}

/* Survives a reload and is shared across tabs, so refreshing the code step does not send a
 * second email and the resend cooldown is honoured in every tab. UX only — the server decides. */
const MFA_CHALLENGE_KEY = 'bcs.mfaChallenge'
/* Written on success purely to wake other tabs through the `storage` event. */
const MFA_VERIFIED_KEY = 'bcs.mfaVerifiedAt'

/**
 * Does the TOKEN already prove this session passed the second factor? A local JWT read, no
 * network. It can only prove the positive: a token without the claim may simply belong to an
 * environment where enforcement is off, which only the server knows (GET /mfa/status).
 */
async function readMfaPendingFromToken(currentUser) {
  if (!currentUser) return false
  const { claims } = await currentUser.getIdTokenResult()
  const authTime = Number(claims.auth_time)
  if (!Number.isFinite(authTime)) return false
  if (claims.mfaExempt === true) return false
  const sessions = Array.isArray(claims.mfaSessions) ? claims.mfaSessions.map(Number) : []
  return !sessions.includes(authTime)
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const loading = ref(false)
  const error = ref('')
  const isAuthenticated = ref(false)
  const isRefreshing = ref(false)
  const refreshSubscribers = ref([])
  /* True between a successful password sign-in and a successful code verification. */
  const mfaPending = ref(false)
  /* null = the server has not been asked yet; false = it said this user does not need a code. */
  const mfaRequirementKnown = ref(null)
  const mfaMaskedEmail = ref('')
  const mfaExpiryMinutes = ref(10)
  /* When the sign-in is paused, the instant it resumes. Derived from the server's remaining
   * seconds on every status read, so a reload restores the pause instead of losing it. */
  const mfaLockedUntil = ref(0)
  /* The server accepted this sign-in's code but the session token has not been adopted yet. A
   * retry must then skip the code (it is spent) and go straight to the session exchange. */
  let mfaCodeAccepted = false
  let sessionUpgradeAttempted = false

  const effectivePermissionKeys = ref([])
  const roleKeys = ref([])
  const moduleKeys = ref([])
  const capCompany = ref(null)
  const accessLoaded = ref(false)
  const userProfile = ref(null)
  let userProfileLoadPromise = null

  function onRefreshed(token) {
    refreshSubscribers.value.forEach(callback => callback(token))
    refreshSubscribers.value = []
  }

  function subscribeToTokenRefresh(callback) {
    refreshSubscribers.value.push(callback)
  }

  function applyAccessFromProfile(profile = {}) {
    const access = profile.access || {}
    effectivePermissionKeys.value = access.effectivePermissionKeys || []
    roleKeys.value = access.roleKeys || []
    moduleKeys.value = access.moduleKeys || []
    capCompany.value = profile.CAPCompany ?? null
    accessLoaded.value = true
  }

  function clearAccess() {
    effectivePermissionKeys.value = []
    roleKeys.value = []
    moduleKeys.value = []
    capCompany.value = null
    accessLoaded.value = false
    userProfile.value = null
    userProfileLoadPromise = null
  }

  /**
   * Loads the authenticated user's profile from the API.
   * Caches the result and deduplicates concurrent calls.
   *
   * @param {{ force?: boolean }} options
   * @returns {Promise<object|null>}
   */
  async function fetchUserProfile({ force = false } = {}) {
    if (!force && userProfile.value) {
      return userProfile.value
    }
    if (userProfileLoadPromise && !force) {
      return userProfileLoadPromise
    }

    const loadProfile = async () => {
      const auth = getAuth()
      const currentUser = auth.currentUser
      if (!currentUser) {
        clearAccess()
        return null
      }

      const token = await currentUser.getIdToken()
      localStorage.setItem('userToken', token)

      const { simpleGet } = RequestService()
      const response = await simpleGet('fetchUserProfile', userProfileApiUrl())
      if (!response) {
        return userProfile.value
      }

      const profile = response.data?.object || response.data?.data?.object || null
      if (profile) {
        userProfile.value = profile
        applyAccessFromProfile(profile)
      }
      return profile
    }

    userProfileLoadPromise = loadProfile()
      .finally(() => {
        userProfileLoadPromise = null
      })

    return userProfileLoadPromise
  }

  async function loadAccess(options = {}) {
    await fetchUserProfile(options)
    return effectivePermissionKeys.value
  }

  async function ensureAccessLoaded() {
    if (accessLoaded.value && userProfile.value) {
      return effectivePermissionKeys.value
    }
    await fetchUserProfile()
    return effectivePermissionKeys.value
  }

  function hasPermissionKey(permissionKey) {
    if (checkPlatformAdmin(effectivePermissionKeys.value)) {
      return true
    }
    return hasPermission(effectivePermissionKeys.value, permissionKey)
  }

  function hasAnyPermissionKeys(permissionKeys) {
    if (checkPlatformAdmin(effectivePermissionKeys.value)) {
      return true
    }
    return hasAnyPermission(effectivePermissionKeys.value, permissionKeys)
  }

  function hasAllPermissionKeys(permissionKeys) {
    if (checkPlatformAdmin(effectivePermissionKeys.value)) {
      return true
    }
    return hasAllPermissions(effectivePermissionKeys.value, permissionKeys)
  }

  async function login(username, password) {
    loading.value = true
    error.value = ''
    mfaCodeAccepted = false
    sessionUpgradeAttempted = false
    // A password sign-in is always a new session: a code sent to an earlier one is useless here.
    clearMfaChallengeState()

    try {
      const auth = getAuth()
      const userCredential = await signInWithEmailAndPassword(auth, username, password)
      user.value = userCredential.user
      isAuthenticated.value = true
      const uid = userCredential.user.uid
      const token = await userCredential.user.getIdToken()
      localStorage.setItem('userToken', token)
      localStorage.setItem('userUid', uid)
      // Correlates Sentry events (navigation anomalies included) with the
      // session. Only the opaque uid — no email or other PII.
      Sentry.setUser({ id: uid })

      /* The password session exists but is not yet trusted: it carries no `bcsSession` claim, so
       * /users/me would answer 403 mfa_required and the Security Rules refuse it too. Exchange it
       * for a backend-minted one. When the second factor is still owed, the profile fetch and the
       * audit entry wait until the code is verified rather than failing on every login. */
      let sessionToken = token
      try {
        const session = await ensureBackendSession()
        if (session.required) {
          mfaPending.value = true
          mfaRequirementKnown.value = true
          return { success: true, mfaRequired: true }
        }
        if (session.token) sessionToken = session.token
      } catch (sessionError) {
        /* The exchange is unavailable (the endpoint is down). The status check still decides
         * whether the code step is owed; the router guard retries the exchange on the next
         * navigation (ensureTrustedSession), since without it direct SDK access is refused. */
        handleErrors('mfaSession', sessionError)
        if (await refreshMfaPending()) {
          return { success: true, mfaRequired: true }
        }
      }

      await fetchUserProfile({ force: true })
      logLoginSuccess(sessionToken)
      return { success: true, mfaRequired: false }
    } catch (err) {
      error.value = err.message
      handleErrors(err.code, err)
      logLoginFailed(username)
      return { success: false, error: err }
    } finally {
      loading.value = false
    }
  }

  async function refreshToken() {
    if (isRefreshing.value) {
      return new Promise(resolve => {
        subscribeToTokenRefresh(token => {
          resolve(token)
        })
      })
    }

    isRefreshing.value = true

    try {
      const auth = getAuth()
      const currentUser = auth.currentUser
      if (!currentUser) {
        throw new Error('No user found')
      }

      const newToken = await currentUser.getIdToken(true)
      localStorage.setItem('userToken', newToken)
      await fetchUserProfile({ force: true })
      isRefreshing.value = false
      onRefreshed(newToken)
      logTokenRefresh(newToken)
      return newToken
    } catch (err) {
      isRefreshing.value = false
      handleErrors('refreshToken', err)
      logout()
      throw err
    }
  }

  /* ── Two-factor authentication ─────────────────────────────────────────── */

  /**
   * Is this session still missing its second factor?
   *
   * Fast path first: a token carrying the claim proves verification with no network call, which
   * is what lets the router ask on every navigation. Only when the token cannot prove it does
   * this ask the server, because "no claim" also describes an environment where enforcement is
   * simply off. The server's answer is remembered for the page session; if enforcement is
   * switched on underneath us, the 403 interceptor flags it immediately anyway.
   *
   * Never throws, and fails OPEN on the client: the API is the enforcer, so a failed status
   * check must not strand a user on a code step the backend is not going to ask for.
   */
  async function refreshMfaPending() {
    try {
      const auth = getAuth()
      const currentUser = auth.currentUser
      if (!currentUser) {
        mfaPending.value = false
        return false
      }

      if (!(await readMfaPendingFromToken(currentUser))) {
        mfaPending.value = false
        return false
      }

      if (mfaRequirementKnown.value === false) {
        mfaPending.value = false
        return false
      }

      const status = await fetchMfaStatus()
      mfaRequirementKnown.value = status.required
      mfaPending.value = status.required
      return mfaPending.value
    } catch (err) {
      handleErrors('refreshMfaPending', err)
      mfaPending.value = false
      return false
    }
  }

  /** GET /mfa/status — the only place that knows whether enforcement is on for this user. */
  async function fetchMfaStatus() {
    const { simpleGet } = RequestService()
    const response = await simpleGet('mfaStatus', mfaApiUrl('status'), { rethrow: true })
    const data = response?.data?.data || {}
    if (data.maskedEmail) mfaMaskedEmail.value = data.maskedEmail
    if (data.expiryMinutes) mfaExpiryMinutes.value = Number(data.expiryMinutes)
    /* Anchor the countdown to OUR clock from the server's remaining seconds. Storing the server's
     * absolute instant instead would let a skewed client clock cut the pause short or stretch it. */
    const retryAfter = Number(data.retryAfterSeconds) || 0
    mfaLockedUntil.value = retryAfter > 0 ? Date.now() + retryAfter * 1000 : 0
    return { required: data.required === true, ...data }
  }

  /**
   * Swap whatever session we hold for one the backend minted, carrying the `bcsSession` claim.
   * That claim is what the Security Rules read, so until this runs the browser's direct Firestore,
   * RTDB and Storage access is not trusted.
   */
  async function adoptSessionToken(sessionToken) {
    const auth = getAuth()
    const credential = await signInWithCustomToken(auth, sessionToken)
    user.value = credential.user
    isAuthenticated.value = true
    const token = await credential.user.getIdToken()
    localStorage.setItem('userToken', token)
    localStorage.setItem('userUid', credential.user.uid)
    return token
  }

  /**
   * POST /mfa/session — the exchange every sign-in goes through, including the ones that never see
   * a code (exempt accounts, and everyone while the emergency lever is off).
   * Answers `{ required: true }` when the second factor is still owed; that is a normal step, not
   * an error, which is why the endpoint returns 200 for it.
   */
  async function ensureBackendSession() {
    const { post } = RequestService()
    const response = await post('mfaSession', mfaApiUrl('session'), {})
    const data = response?.data?.data || {}

    if (data.required === true) {
      if (data.maskedEmail) mfaMaskedEmail.value = data.maskedEmail
      if (data.expiryMinutes) mfaExpiryMinutes.value = Number(data.expiryMinutes)
      return { required: true }
    }
    if (!data.sessionToken) return { required: false, token: null }
    return { required: false, token: await adoptSessionToken(data.sessionToken) }
  }

  /**
   * Obtain the backend session for a sign-in whose second factor already passed on the server.
   * The exchange recognises it as verified and mints; one retry covers a transient mint failure.
   * Throws `mfa_session_unavailable` when no token comes back: the Security Rules accept nothing
   * else, so declaring the sign-in complete without one would land the user on a broken Home.
   */
  async function completeBackendSession(attempts = 2) {
    for (let i = 0; i < attempts; i++) {
      try {
        const session = await ensureBackendSession()
        if (session.token) return session.token
      } catch (err) {
        handleErrors('completeBackendSession', err)
      }
      if (i < attempts - 1) await new Promise((resolve) => setTimeout(resolve, 800 * (i + 1)))
    }
    throw new Error('mfa_session_unavailable')
  }

  /**
   * Upgrade a session that holds no `bcsSession` claim: one opened before the exchange shipped, or
   * one whose exchange failed at sign-in. The Security Rules refuse such a token, so without this
   * those users would lose direct Firestore, RTDB and Storage access until their next sign-in.
   * Tried once per page session; answers `{ required: true }` when the second factor is still owed.
   */
  async function ensureTrustedSession() {
    const currentUser = getAuth().currentUser
    if (!currentUser || sessionUpgradeAttempted) return { required: false }

    const { claims } = await currentUser.getIdTokenResult()
    if (typeof claims.bcsSession === 'string') return { required: false }

    sessionUpgradeAttempted = true
    try {
      const session = await ensureBackendSession()
      if (session.required) markMfaPending()
      return { required: session.required === true }
    } catch (err) {
      handleErrors('ensureTrustedSession', err)
      return { required: false }
    }
  }

  /** Called by the RequestService interceptor when any call answers 403 mfa_required. */
  function markMfaPending() {
    mfaRequirementKnown.value = true
    mfaPending.value = true
  }

  function readMfaChallengeState() {
    try {
      const raw = localStorage.getItem(MFA_CHALLENGE_KEY)
      if (!raw) return null
      const record = JSON.parse(raw)
      return record?.uid === localStorage.getItem('userUid') ? record : null
    } catch {
      return null
    }
  }

  function clearMfaChallengeState() {
    localStorage.removeItem(MFA_CHALLENGE_KEY)
    localStorage.removeItem(MFA_VERIFIED_KEY)
    // Switching accounts must not carry the previous user's pause onto the next screen.
    mfaLockedUntil.value = 0
  }

  /** Asks the backend to email a code. Also used for resends — same endpoint, same limits. */
  async function requestMfaChallenge() {
    const { post } = RequestService()
    const response = await post('mfaChallenge', mfaApiUrl('challenge'), {})
    const data = response?.data?.data || {}

    /* The server already considers this session verified; the client is just holding a stale
     * token. Complete the session exchange instead of sending another email. */
    if (data.alreadyVerified) {
      await completeBackendSession()
      mfaPending.value = false
      return { alreadyVerified: true }
    }

    if (data.maskedEmail) mfaMaskedEmail.value = data.maskedEmail

    const record = {
      uid: localStorage.getItem('userUid'),
      sentAt: Date.now(),
      resendAvailableAt: Number(data.resendAvailableAt) || Date.now() + 60000,
      expiresAt: Number(data.expiresAt) || Date.now() + 10 * 60000
    }
    localStorage.setItem(MFA_CHALLENGE_KEY, JSON.stringify(record))
    return record
  }

  /**
   * Order matters and is enforced here: verify → adopt the session token → load the profile →
   * only then may the caller navigate. /users/me answers 403 until the session token lands.
   */
  async function verifyMfaCode(code) {
    let sessionToken = null
    if (!mfaCodeAccepted) {
      const { post } = RequestService()
      const response = await post('mfaVerify', mfaApiUrl('verify'), { code })
      mfaCodeAccepted = true
      sessionToken = response?.data?.data?.sessionToken || null
    }

    /* The backend hands back a session carrying the `bcsSession` claim. Adopting it replaces the
     * password session outright, so the claim is present in the very first token. It is required,
     * not optional: the Security Rules accept nothing else. If the mint failed (the response still
     * reports success, the code DID pass) the exchange endpoint is asked for it instead. */
    let token
    try {
      token = sessionToken
        ? await adoptSessionToken(sessionToken)
        : await completeBackendSession()
    } catch (err) {
      handleErrors('verifyMfaCode', err)
      throw new Error('mfa_session_unavailable')
    }
    mfaCodeAccepted = false
    mfaPending.value = false
    localStorage.removeItem(MFA_CHALLENGE_KEY)
    localStorage.setItem(MFA_VERIFIED_KEY, String(Date.now()))

    await fetchUserProfile({ force: true })
    logLoginSuccess(token)
    return true
  }

  function logout() {
    try {
      const auth = getAuth()
      auth.signOut()
      user.value = null
      isAuthenticated.value = false
      clearAccess()
      localStorage.removeItem('userToken')
      localStorage.removeItem('userUid')
      clearMfaChallengeState()
      mfaPending.value = false
      mfaRequirementKnown.value = null
      mfaCodeAccepted = false
      sessionUpgradeAttempted = false
      Sentry.setUser(null)
    } catch (error) {
      handleErrors('logout', error)
      throw error
    }
  }

  /** Validates Firebase session and refreshes the stored ID token only. */
  async function checkAuth() {
    try {
      const auth = getAuth()
      const currentUser = auth.currentUser

      if (!currentUser) {
        return false
      }

      const token = await currentUser.getIdToken()
      const uid = currentUser.uid

      localStorage.setItem('userToken', token)
      localStorage.setItem('userUid', uid)
      // Restores user correlation on session revalidation (e.g. page reload,
      // where login() never runs). Opaque uid only — no PII.
      Sentry.setUser({ id: uid })
      isAuthenticated.value = true
      return true
    } catch (error) {
      handleErrors('checkAuth', error)
      isAuthenticated.value = false
      return false
    }
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    effectivePermissionKeys,
    roleKeys,
    moduleKeys,
    capCompany,
    accessLoaded,
    userProfile,
    applyAccessFromProfile,
    fetchUserProfile,
    loadAccess,
    ensureAccessLoaded,
    hasPermission: hasPermissionKey,
    hasAnyPermission: hasAnyPermissionKeys,
    hasAllPermissions: hasAllPermissionKeys,
    login,
    logout,
    checkAuth,
    refreshToken,
    mfaPending,
    mfaMaskedEmail,
    mfaExpiryMinutes,
    mfaLockedUntil,
    refreshMfaPending,
    ensureTrustedSession,
    fetchMfaStatus,
    markMfaPending,
    requestMfaChallenge,
    verifyMfaCode,
    readMfaChallengeState,
    clearMfaChallengeState
  }
})
